import { Sparkles } from 'lucide-react';
import { Fragment } from 'react';

import {
    StatusBadge,
    type StatusConfig,
} from '@apps/web/components/ui/status_badge';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { type Outreach } from '@libraries/typescript/api_client';

type OutreachType = Outreach['outreach_type'];

export const OUTREACH_TYPE_CONFIG: Record<OutreachType, StatusConfig> = {
    callout: {
        label: 'Open Shift',
        dotColor: 'bg-blue-500',
        backgroundColor: 'bg-blue-100 dark:bg-blue-950/50',
        textColor: 'text-blue-700 dark:text-blue-300',
    },
    recurring_shift: {
        label: 'Recurring Shift',
        dotColor: 'bg-indigo-500',
        backgroundColor: 'bg-indigo-100 dark:bg-indigo-950/50',
        textColor: 'text-indigo-700 dark:text-indigo-300',
    },
    clock_in: {
        label: 'Clock In',
        dotColor: 'bg-amber-500',
        backgroundColor: 'bg-amber-100 dark:bg-amber-950/50',
        textColor: 'text-amber-700 dark:text-amber-300',
    },
    clock_out: {
        label: 'Clock Out',
        dotColor: 'bg-orange-600',
        backgroundColor: 'bg-orange-100 dark:bg-orange-950/50',
        textColor: 'text-orange-700 dark:text-orange-300',
    },
    plan_of_care_duties: {
        label: 'Plan of Care Tasks',
        dotColor: 'bg-purple-500',
        backgroundColor: 'bg-purple-100 dark:bg-purple-950/50',
        textColor: 'text-purple-700 dark:text-purple-300',
    },
    shift_confirmation: {
        label: 'Shift Confirmation',
        dotColor: 'bg-emerald-500',
        backgroundColor: 'bg-emerald-100 dark:bg-emerald-950/50',
        textColor: 'text-emerald-700 dark:text-emerald-300',
    },
    survey: {
        label: 'Survey',
        dotColor: 'bg-teal-500',
        backgroundColor: 'bg-teal-100 dark:bg-teal-950/50',
        textColor: 'text-teal-700 dark:text-teal-300',
    },
    message: {
        label: 'Message',
        dotColor: 'bg-slate-500',
        backgroundColor: 'bg-muted',
        textColor: 'text-muted-foreground',
    },
    shift_discovery: {
        label: 'Caregiver Shift Request',
        dotColor: 'bg-indigo-500',
        backgroundColor: 'bg-indigo-100 dark:bg-indigo-950/50',
        textColor: 'text-indigo-700 dark:text-indigo-300',
    },
};

const URGENT_OUTREACH_STATUS: StatusConfig = {
    label: 'Urgent',
    dotColor: 'bg-rose-500',
    backgroundColor: 'bg-rose-100 dark:bg-rose-950/50',
    textColor: 'text-rose-700 dark:text-rose-300',
};

type OutreachTypeBadgeProps = {
    readonly outreachType: OutreachType;
    readonly className?: string;
    readonly showSuggestedCaregiversIndicator?: boolean;
    readonly isUrgent?: boolean;
};

const SUGGESTED_CAREGIVERS_TOOLTIP =
    'Phoebe has suggested caregivers for this open shift.';

export function OutreachTypeBadge({
    outreachType,
    className,
    showSuggestedCaregiversIndicator = false,
    isUrgent = false,
}: OutreachTypeBadgeProps) {
    const config = OUTREACH_TYPE_CONFIG[outreachType];

    return (
        <Fragment>
            <StatusBadge status={config} className={className} />
            {isUrgent ? <StatusBadge status={URGENT_OUTREACH_STATUS} /> : null}
            {showSuggestedCaregiversIndicator ? (
                <Tooltip>
                    <TooltipTrigger asChild={true}>
                        <span
                            aria-label={SUGGESTED_CAREGIVERS_TOOLTIP}
                            className="cursor-help text-violet-500"
                        >
                            <Sparkles
                                aria-hidden={true}
                                className="h-3.5 w-3.5"
                            />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        {SUGGESTED_CAREGIVERS_TOOLTIP}
                    </TooltipContent>
                </Tooltip>
            ) : null}
        </Fragment>
    );
}
